// 생육보고서 카드 — 최근 생육 기록 요약 + 사진 썸네일 + 재촬영(CAPTURE) 버튼.
// 장비모델이 growthEnabled 일 때만 부모(DataView)가 마운트한다.
// 재촬영은 서버가 MQTT 로 명령을 발행하고 장비가 서버로 업로드하므로,
// 화면을 나가도 진행된다 — 이 카드는 ACK 를 폴링해 새 사진만 새로고침한다.
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeviceCommandRecord, DeviceInfo, GrowthRecord, GrowthStage } from '@grosync/common';
import { Sym } from '../../ui';
import { fetchLatestGrowthRecord, getPhotoUrl } from '../../api/growth';
import { fetchDeviceCommands, sendDeviceCommand } from '../../api/commands';
import { apiGetBlob } from '../../api/client';
import { resolveCaptureWait } from './captureWait';

const STAGE_LABEL: Record<GrowthStage, string> = {
  seedling: '모종',
  vegetative: '생장',
  flowering: '개화',
  fruiting: '결실',
  harvest: '수확',
};

const CROP_LABEL: Record<string, string> = {
  tomato: '토마토',
  pepper: '고추',
  lettuce: '상추',
  strawberry: '딸기',
  cucumber: '오이',
};

function cropLabel(c: string): string {
  return CROP_LABEL[c] ?? c;
}

/** 인증 헤더로 사진을 받아 objectURL 로 표시 (이미지 엔드포인트가 Bearer 헤더만 받음). */
function useAuthedImage(photoPath: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!photoPath) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let obj: string | null = null;
    apiGetBlob(getPhotoUrl(photoPath))
      .then((blob) => {
        if (cancelled) return;
        obj = URL.createObjectURL(blob);
        setUrl(obj);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [photoPath]);
  return url;
}

function PhotoThumb({ photoPath }: { photoPath: string | null }): React.ReactElement {
  const src = useAuthedImage(photoPath);
  return (
    <div
      style={{
        width: 76,
        height: 76,
        flexShrink: 0,
        borderRadius: 14,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: src
          ? `center/cover no-repeat url(${src})`
          : 'linear-gradient(160deg, var(--md-sys-color-tertiary) 0%, #5f8f4a 100%)',
      }}
    >
      {!src && <Sym name="local_florist" size={34} fill={1} style={{ color: 'rgba(255,255,255,0.9)' }} />}
    </div>
  );
}

interface CaptureNotice {
  kind: 'ok' | 'warn' | 'error';
  text: string;
}

export default function GrowthReportCard({ device }: { device: DeviceInfo }): React.ReactElement {
  const qc = useQueryClient();
  const growthQ = useQuery<GrowthRecord | null>({
    queryKey: ['growth-latest', device.id],
    queryFn: () => fetchLatestGrowthRecord(device.id),
    retry: false,
  });
  const rec = growthQ.data ?? null;

  // ── 재촬영 — 명령 발행 후 ACK 폴링, 완료 시 사진 새로고침 ──
  const [wait, setWait] = useState<{ id: string; startedAt: number } | null>(null);
  const [notice, setNotice] = useState<CaptureNotice | null>(null);

  // 장비를 바꾸면 이전 장비의 대기·안내는 버린다 (명령 자체는 서버에서 계속 진행)
  useEffect(() => {
    setWait(null);
    setNotice(null);
  }, [device.id]);

  const captureMut = useMutation({
    mutationFn: () => sendDeviceCommand(device.id, { type: 'CAPTURE' }),
    onSuccess: (record) => {
      if (record.status === 'failed') {
        setNotice({ kind: 'error', text: `명령 전송 실패${record.error ? `: ${record.error}` : ''}` });
        return;
      }
      setNotice(null);
      setWait({ id: record.id, startedAt: Date.now() });
    },
    onError: (e) => setNotice({ kind: 'error', text: `명령 전송 실패: ${(e as Error).message}` }),
  });

  const cmdsQ = useQuery<DeviceCommandRecord[]>({
    queryKey: ['device-commands', device.id],
    queryFn: () => fetchDeviceCommands(device.id),
    enabled: !!wait,
    refetchInterval: 3_000,
  });

  const waitPhase = wait
    ? resolveCaptureWait(cmdsQ.data, wait.id, wait.startedAt, Date.now())
    : null;

  useEffect(() => {
    if (!waitPhase || waitPhase.phase === 'waiting') return;
    if (waitPhase.phase === 'acked') {
      void qc.invalidateQueries({ queryKey: ['growth-latest', device.id] });
      void qc.invalidateQueries({ queryKey: ['growth'] });
      setNotice({ kind: 'ok', text: '새 사진을 받았습니다.' });
    } else if (waitPhase.phase === 'failed') {
      setNotice({ kind: 'error', text: `촬영 실패${waitPhase.error ? `: ${waitPhase.error}` : ''}` });
    } else {
      setNotice({ kind: 'warn', text: '장비가 아직 응답하지 않습니다 — 사진은 도착하면 자동 반영됩니다.' });
    }
    setWait(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitPhase?.phase]);

  const waiting = !!wait || captureMut.isPending;

  const metrics =
    rec &&
    [
      rec.plantHeight != null ? `키 ${rec.plantHeight}cm` : null,
      rec.leafCount != null ? `잎 ${rec.leafCount}장` : null,
      rec.fruitCount != null ? `열매 ${rec.fruitCount}개` : null,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <div
      style={{
        borderRadius: 18,
        padding: '16px 18px',
        minHeight: 148,
        background: 'var(--md-sys-color-tertiary-container)',
        color: 'var(--md-sys-color-on-tertiary-container)',
        border: '1px solid var(--md-sys-color-outline-variant)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Sym name="potted_plant" size={20} fill={1} style={{ color: 'var(--md-sys-color-tertiary)' }} />
        <span style={{ font: '700 15px/20px var(--gs-font-kr)' }}>생육보고서</span>
        {rec?.growthStage && (
          <span
            className="gs-tag"
            style={{ background: 'rgba(0,0,0,0.08)', color: 'var(--md-sys-color-on-tertiary-container)', height: 18, fontSize: 10 }}
          >
            {STAGE_LABEL[rec.growthStage]}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {rec && (
          <span className="gs-num" style={{ font: '400 11px/14px var(--gs-font-kr)', opacity: 0.75 }}>
            {rec.recordedAt}
          </span>
        )}
        <button
          type="button"
          disabled={waiting}
          onClick={() => captureMut.mutate()}
          title="센서cam 에 즉시 촬영을 요청하고 새 사진을 받아옵니다"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            height: 26, padding: '0 10px', borderRadius: 9999,
            border: '1px solid var(--md-sys-color-outline-variant)',
            background: 'rgba(255,255,255,0.45)',
            color: 'var(--md-sys-color-on-tertiary-container)',
            font: '600 11px/1 var(--gs-font-kr)',
            cursor: waiting ? 'default' : 'pointer',
            opacity: waiting ? 0.7 : 1,
          }}
        >
          <Sym name={waiting ? 'hourglass_top' : 'photo_camera'} size={13} fill={1} />
          {waiting ? '수신 대기중…' : '재촬영'}
        </button>
      </div>

      {/* body */}
      {growthQ.isLoading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', font: '400 13px/18px var(--gs-font-kr)', opacity: 0.7 }}>
          생육 데이터 불러오는 중…
        </div>
      ) : !rec ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, font: '500 13px/18px var(--gs-font-kr)', opacity: 0.8 }}>
          <Sym name="eco" size={18} />
          아직 생육 기록이 없습니다.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 12, flex: 1 }}>
          <PhotoThumb photoPath={rec.photoPath} />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center' }}>
            <div style={{ font: '600 14px/18px var(--gs-font-kr)' }}>
              {cropLabel(rec.cropType)}
              {rec.growthStage ? ` · ${STAGE_LABEL[rec.growthStage]}` : ''}
            </div>
            {metrics && (
              <div className="gs-num" style={{ font: '500 13px/18px var(--gs-font-kr)', opacity: 0.85 }}>{metrics}</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, font: '600 13px/18px var(--gs-font-kr)' }}>
              <Sym name="favorite" size={14} fill={1} style={{ color: 'var(--md-sys-color-tertiary)' }} />
              건강점수 <span className="gs-num">{rec.healthScore != null ? `${rec.healthScore}/5` : '—'}</span>
            </div>
          </div>
        </div>
      )}

      {/* 재촬영 진행·결과 안내 */}
      {(waiting || notice) && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            font: '500 12px/16px var(--gs-font-kr)',
            color:
              notice?.kind === 'error' ? 'var(--md-sys-color-error)'
                : notice?.kind === 'warn' ? 'var(--gs-warning)'
                  : 'var(--md-sys-color-on-tertiary-container)',
            opacity: notice ? 1 : 0.8,
          }}
        >
          <Sym
            name={waiting ? 'hourglass_top' : notice?.kind === 'ok' ? 'check_circle' : notice?.kind === 'warn' ? 'schedule' : 'error'}
            size={14}
            fill={1}
          />
          {waiting ? '촬영·수신 대기중… 화면을 나가도 촬영은 계속 진행됩니다.' : notice?.text}
        </div>
      )}
    </div>
  );
}
